import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Check, CreditCard, ExternalLink, Info, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CREATE_AUTOMATIC_BILLING_SETUP_SESSION,
  CREATE_BILLING_PORTAL_SESSION,
  CREATE_SUBSCRIPTION_CHECKOUT_SESSION,
  SUBMIT_ENTERPRISE_INQUIRY,
  UPDATE_TENANT_SETTINGS,
} from "@/graphql/mutations";
import { PREVIEW_PLAN_CHANGE } from "@/graphql/queries";
import { useToast } from "@/hooks/use-toast";
import { trackDashboardEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import type { BillingCapabilities, BillingProvider, PlanChangePreview, PlanConfig, SubscriptionPlan } from "../types";
import { billingPortalReturnUrl } from "./billingUrls";
import {
  formatRooms,
  getPlanConfig,
  getPlanMetrics,
  isUpgradePath,
  PlanMetrics,
  PLAN_DISPLAY_NAMES,
  PLAN_FEATURES,
  PLAN_META,
  PLAN_OPTIONS,
  PLAN_ORDER,
  PlanOption,
} from "./planCatalog";

const SUPPORT_EMAIL = "support@flowlabs.live";

interface CreateSubscriptionCheckoutSessionResponse {
  createSubscriptionCheckoutSession?: {
    success?: boolean;
    url?: string | null;
    error?: string | null;
  } | null;
}

interface CreateBillingPortalSessionResponse {
  createBillingPortalSession?: {
    success?: boolean;
    url?: string | null;
    error?: string | null;
  } | null;
}

interface CreateAutomaticBillingSetupSessionResponse {
  createAutomaticBillingSetupSession?: {
    success?: boolean;
    url?: string | null;
    error?: string | null;
  } | null;
}

interface SubmitEnterpriseInquiryResponse {
  submitEnterpriseInquiry?: {
    success?: boolean;
    error?: string | null;
  } | null;
}

interface UpdateTenantSettingsResponse {
  updateTenantSettings?: {
    success?: boolean;
    error?: string | null;
  } | null;
}

type CancellationBannerData = {
  title: string;
  body: string;
  action: string;
};

export interface ManagePlanTabProps {
  currentPlan: SubscriptionPlan;
  currentBillingProvider: BillingProvider;
  pendingBillingProvider?: BillingProvider | null;
  billingProviderSwitchAt?: string | null;
  currentMonthlyCredits: number;
  currentPersonalRooms: number;
  currentPriceUsd?: number;
  billingCycleEnd?: string;
  currentSubscriptionCredits?: number;
  currentPlanCreditsRemaining?: number;
  currentRolloverCredits?: number;
  currentPrepaidCredits?: number;
  planConfigs: PlanConfig[];
  billingCapabilities: BillingCapabilities | null;
  organizationId?: string | null;
  showManualBillingBanner?: boolean;
  cancellationBanner?: CancellationBannerData | null;
  showCancellationAction?: boolean;
}

function formatUsdStandard(amount: number) {
  return `$${amount.toFixed(2)}`;
}

function formatDisplayDate(dateString?: string | null) {
  if (!dateString) return null;
  const normalized = dateString.includes("T") ? dateString : `${dateString}T00:00:00`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDisplayDateFromDate(date: Date) {
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

type PendingSummaryRow = {
  label: string;
  value: string;
  previousValue?: string;
  helper?: string;
  noWrap?: boolean;
  tone?: "default" | "muted";
};

type PendingPlanSummary = {
  title: string;
  confirmLabel: string;
  confirmDisabled: boolean;
  dueToday: string;
  dueDescription: string;
  immediateHeading?: string | null;
  immediateValue?: string;
  immediateNote?: string;
  immediateTone?: "accent" | "default";
  secondaryHeading?: string;
  secondaryRows?: PendingSummaryRow[];
  supportingNote?: string;
  immediateText?: string;
  renewalEyebrow?: string | null;
  renewalDate: string;
  renewalSummary?: string;
  renewalSubnote?: string;
  renewalRows: PendingSummaryRow[];
};

const PLAN_GRADIENT_WASH: Record<PlanOption, string> = {
  PERSONAL: "from-slate-400/18 via-slate-300/8 to-transparent",
  PRO: "from-amber-400/20 via-orange-400/10 to-transparent",
  STUDIO: "from-purple-400/20 via-indigo-400/10 to-transparent",
};

function PlanOptionCard({
  plan,
  currentPlan,
  metrics,
  isPending,
  isSubmitting,
  actionLabel,
  actionDisabled,
  onChoosePlan,
}: {
  plan: PlanOption;
  currentPlan: SubscriptionPlan;
  metrics: PlanMetrics;
  isPending: boolean;
  isSubmitting: boolean;
  actionLabel: string;
  actionDisabled?: boolean;
  onChoosePlan: (plan: PlanOption) => void;
}) {
  const meta = PLAN_META[plan];
  const isCurrent = plan === currentPlan;
  const roomLabel = metrics.personalRooms === 1 ? "personal room" : "personal rooms";
  const displayFeatures = useMemo(() => {
    const baseFeatures = PLAN_FEATURES[plan];
    if (plan !== "PRO" && plan !== "STUDIO") {
      return baseFeatures;
    }

    const rolloverCredits = metrics.monthlyCredits * 2;
    return [
      baseFeatures[0],
      baseFeatures[1],
      `Rollover unused credits (max ${rolloverCredits.toLocaleString()})`,
      ...baseFeatures.slice(2),
    ];
  }, [metrics.monthlyCredits, plan]);

  return (
    <article
      className={cn(
        "relative isolate overflow-hidden flex h-full flex-col rounded-lg border bg-card p-5 transition-colors",
        isCurrent ? meta.outline : "border-border",
        isPending ? "ring-1 ring-primary/40" : "",
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute -right-12 -top-12 z-0 h-36 w-36 rounded-full bg-gradient-to-br blur-3xl",
          PLAN_GRADIENT_WASH[plan],
        )}
      />

      <header className="relative z-10 min-h-[112px] space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-2xl font-semibold tracking-tight text-foreground">
            {PLAN_DISPLAY_NAMES[plan]}
          </h3>
          <span className="inline-flex min-h-[18px] items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
            {isCurrent ? (
              <>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Current plan
              </>
            ) : null}
          </span>
        </div>

        <div className="space-y-1.5">
          <p className="text-[38px] font-semibold tracking-tight text-foreground leading-none">
            {formatUsdStandard(metrics.displayPrice)}
          </p>
          <p className="text-sm italic text-muted-foreground">{meta.descriptor}</p>
        </div>
      </header>

      <div className="relative z-10 mt-1 min-h-[78px] space-y-0.5 text-sm text-foreground">
        <p>
          <span className="font-semibold">{metrics.monthlyCredits.toLocaleString()}</span> monthly credits
        </p>
        <p className="italic text-muted-foreground">{metrics.rate} credits / viewer hr</p>
        <p>
          {formatRooms(metrics.personalRooms)} {roomLabel}
        </p>
      </div>

      <div className="relative z-10 my-4 h-px w-full bg-border" />

      <div className="relative z-10 flex-1 space-y-2.5">
        <p className="text-sm font-medium text-foreground">Features</p>
        <ul className="space-y-1.5">
          {displayFeatures.map((feature) => {
            const largeSessionsMatch = feature.match(/^Large sessions - (.+)$/);
            const isLargeSessionsFeature = Boolean(largeSessionsMatch);
            const largeSessionsRate = largeSessionsMatch?.[1] ?? "";

            return (
              <li key={feature} className="flex items-start gap-2 text-sm leading-5 text-muted-foreground">
                <Check className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", meta.accentText)} />
                {isLargeSessionsFeature ? (
                  <span className="inline-flex flex-col gap-0.5">
                    <span className="leading-5 text-foreground">Large sessions (6-10 viewers)</span>
                    <span className="ml-1.5 italic text-muted-foreground">{largeSessionsRate}</span>
                  </span>
                ) : (
                  <span>{feature}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="relative z-10 mt-5 flex justify-end">
        {isCurrent ? (
          <span className="inline-flex min-h-[36px]" />
        ) : (
          <Button
            variant="outline"
            className="h-9 w-full rounded-full border-border bg-transparent px-5 text-sm text-foreground hover:bg-muted/40 md:w-auto"
            onClick={() => onChoosePlan(plan)}
            disabled={isSubmitting || actionDisabled}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Working...
              </>
            ) : (
              actionLabel
            )}
          </Button>
        )}
      </div>
    </article>
  );
}

function EnterpriseRail({
  isSubmitting,
  onContactSales,
}: {
  isSubmitting: boolean;
  onContactSales: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-[linear-gradient(115deg,rgba(21,29,61,0.18)_0%,rgba(25,49,89,0.14)_52%,rgba(30,62,103,0.10)_100%)] p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <p className="text-lg font-semibold tracking-tight text-foreground">Need Enterprise?</p>
          <p className="text-sm text-muted-foreground">Custom scale, controls, and dedicated support.</p>
        </div>

        <Button
          variant="outline"
          className="h-9 w-full rounded-full border-border bg-transparent px-5 text-sm text-foreground hover:bg-muted/40 lg:w-auto"
          onClick={onContactSales}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Opening...
            </>
          ) : (
            <>
              <MessageSquare className="mr-2 h-4 w-4" />
              Contact sales
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export function ManagePlanTab({
  currentPlan,
  currentBillingProvider,
  pendingBillingProvider,
  billingProviderSwitchAt,
  currentMonthlyCredits,
  currentPersonalRooms,
  currentPriceUsd,
  billingCycleEnd,
  currentSubscriptionCredits,
  currentPlanCreditsRemaining,
  currentRolloverCredits = 0,
  currentPrepaidCredits = 0,
  planConfigs,
  billingCapabilities,
  organizationId,
  showManualBillingBanner = true,
  cancellationBanner,
  showCancellationAction = false,
}: ManagePlanTabProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingPlan, setSubmittingPlan] = useState<SubscriptionPlan | null>(null);
  const [pendingPlan, setPendingPlan] = useState<PlanOption | null>(null);
  const { toast } = useToast();

  const [createSubscriptionCheckoutSession] = useMutation<CreateSubscriptionCheckoutSessionResponse>(
    CREATE_SUBSCRIPTION_CHECKOUT_SESSION,
  );
  const [createAutomaticBillingSetupSession] = useMutation<CreateAutomaticBillingSetupSessionResponse>(
    CREATE_AUTOMATIC_BILLING_SETUP_SESSION,
  );
  const [createBillingPortalSession] = useMutation<CreateBillingPortalSessionResponse>(
    CREATE_BILLING_PORTAL_SESSION,
  );
  const [submitEnterpriseInquiry] = useMutation<SubmitEnterpriseInquiryResponse>(SUBMIT_ENTERPRISE_INQUIRY);
  const [updateTenantSettings] = useMutation<UpdateTenantSettingsResponse>(UPDATE_TENANT_SETTINGS);
  const [fetchPreview, { data: previewData }] = useLazyQuery<{
    previewPlanChange: PlanChangePreview | null;
  }>(PREVIEW_PLAN_CHANGE, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (pendingPlan && pendingPlan !== currentPlan) {
      fetchPreview({ variables: { targetPlan: pendingPlan } });
    }
  }, [pendingPlan, currentPlan, fetchPreview]);

  const currentBaseConfig = getPlanConfig(currentPlan, planConfigs);
  const currentMonthlyPrice =
    typeof currentPriceUsd === "number" && Number.isFinite(currentPriceUsd)
      ? currentPriceUsd
      : (currentBaseConfig?.basePrice ?? 0);

  const metricsByPlan = useMemo<Record<PlanOption, PlanMetrics>>(() => {
    const baseMetrics: Record<PlanOption, PlanMetrics> = {
      PERSONAL: getPlanMetrics("PERSONAL", planConfigs),
      PRO: getPlanMetrics("PRO", planConfigs),
      STUDIO: getPlanMetrics("STUDIO", planConfigs),
    };

    if (currentPlan !== "ENTERPRISE") {
      const currentPlanOption = currentPlan as PlanOption;
      baseMetrics[currentPlanOption] = {
        ...baseMetrics[currentPlanOption],
        monthlyPrice: currentMonthlyPrice,
        displayPrice: currentMonthlyPrice,
        monthlyCredits: Math.max(currentMonthlyCredits, baseMetrics[currentPlanOption].monthlyCredits),
        personalRooms: currentPersonalRooms,
      };
    }

    return baseMetrics;
  }, [currentMonthlyCredits, currentMonthlyPrice, currentPersonalRooms, currentPlan, planConfigs]);

  const isSubmittingByPlan: Record<SubscriptionPlan, boolean> = {
    PERSONAL: isSubmitting && submittingPlan === "PERSONAL",
    PRO: isSubmitting && submittingPlan === "PRO",
    STUDIO: isSubmitting && submittingPlan === "STUDIO",
    ENTERPRISE: isSubmitting && submittingPlan === "ENTERPRISE",
  };

  const checkoutUnavailableReason =
    billingCapabilities?.subscriptionCheckoutUnavailableReason || "Subscription checkout is unavailable right now.";
  const automaticBillingSetupUnavailableReason =
    billingCapabilities?.automaticBillingSetupUnavailableReason || "Automatic billing setup is unavailable right now.";
  const portalUnavailableReason =
    billingCapabilities?.billingPortalUnavailableReason || "Billing portal is unavailable right now.";
  const paidPlanUnavailableReason =
    billingCapabilities?.subscriptionCheckoutUnavailableReason || portalUnavailableReason;
  const isManualPaidPlan =
    (currentPlan === "PRO" || currentPlan === "STUDIO") && currentBillingProvider === "MANUAL";
  const automaticBillingScheduled = isManualPaidPlan && pendingBillingProvider === "STRIPE";
  const automaticBillingSwitchText =
    formatDisplayDate(billingProviderSwitchAt) ||
    formatDisplayDate(billingCycleEnd) ||
    "your next renewal";
  const canOpenBillingPortal =
    (currentPlan === "PRO" || currentPlan === "STUDIO") && Boolean(billingCapabilities?.billingPortalEnabled);
  const selectedPlanMetrics = pendingPlan ? metricsByPlan[pendingPlan] : null;
  const currentMetrics =
    currentPlan !== "ENTERPRISE"
      ? metricsByPlan[currentPlan as PlanOption]
      : null;
  const isUpgrade =
    pendingPlan !== null ? PLAN_ORDER[pendingPlan] > PLAN_ORDER[currentPlan] : false;

  const availabilityNote =
    currentPlan === "PERSONAL"
      ? (!billingCapabilities?.subscriptionCheckoutEnabled ? checkoutUnavailableReason : null)
      : isManualPaidPlan
        ? automaticBillingScheduled
          ? null
          : (!billingCapabilities?.automaticBillingSetupEnabled ? automaticBillingSetupUnavailableReason : null)
        : (currentPlan === "PRO" || currentPlan === "STUDIO") && !billingCapabilities?.billingPortalEnabled
          ? paidPlanUnavailableReason
          : null;

  const chargeTodayPreview = useMemo(() => {
    if (!pendingPlan || !selectedPlanMetrics) return null;
    if (currentPlan === "PERSONAL") {
      return {
        amountUsd: selectedPlanMetrics.displayPrice,
        description: "Billed when you confirm checkout.",
      };
    }

    if (isManualPaidPlan) {
      return {
        amountUsd: 0,
        description: automaticBillingScheduled
          ? "Automatic billing is already scheduled."
          : "Add a payment method to move this workspace to automatic billing.",
      };
    }

    if (!isUpgrade || !currentMetrics) {
      return {
        amountUsd: 0,
        description: "Stripe confirms the change in billing before anything updates.",
      };
    }

    const amountUsd = Math.max(0, selectedPlanMetrics.displayPrice - currentMetrics.displayPrice);

    return {
      amountUsd,
      description: "Difference between your current plan and the new plan.",
    };
  }, [automaticBillingScheduled, currentMetrics, currentPlan, isManualPaidPlan, isUpgrade, pendingPlan, selectedPlanMetrics]);

  const nextRenewalDateLabel = useMemo(() => {
    if (billingCycleEnd) {
      const parsed = new Date(`${billingCycleEnd}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        parsed.setDate(parsed.getDate() + 1);
        return formatDisplayDateFromDate(parsed);
      }
    }

    if (pendingPlan && currentPlan === "PERSONAL") {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return formatDisplayDateFromDate(nextMonth);
    }

    return "Your next renewal";
  }, [billingCycleEnd, currentPlan, pendingPlan]);

  const pendingSummary = useMemo<PendingPlanSummary | null>(() => {
    if (!pendingPlan || !selectedPlanMetrics) return null;

    const targetName = PLAN_DISPLAY_NAMES[pendingPlan];
    const dueToday = chargeTodayPreview ? formatUsdStandard(chargeTodayPreview.amountUsd) : formatUsdStandard(0);
    const preview = previewData?.previewPlanChange;

    // Use backend preview when available, fall back to client-side estimate.
    const currentAvailableSubscriptionCredits = Math.max(
      preview?.creditsRemaining ?? currentPlanCreditsRemaining ?? currentSubscriptionCredits ?? 0,
      0,
    );
    const usedThisCycle = preview?.usedThisCycle ?? Math.max(currentMonthlyCredits - currentAvailableSubscriptionCredits, 0);
    const rolloverCredits = preview?.rolloverCredits ?? Math.max(currentRolloverCredits, 0);
    const prepaidCredits = preview?.prepaidCredits ?? Math.max(currentPrepaidCredits, 0);
    const totalAvailableAfterUpgrade = preview?.totalAfterUpgrade ?? Math.max(
      currentAvailableSubscriptionCredits + (selectedPlanMetrics.monthlyCredits - currentMonthlyCredits) + rolloverCredits + prepaidCredits,
      0,
    );
    const remainingAfterUpgrade = totalAvailableAfterUpgrade - rolloverCredits - prepaidCredits;
    const upgradeBreakdownRows: PendingSummaryRow[] = [
      {
        label: `${targetName} subscription`,
        value: remainingAfterUpgrade.toLocaleString(),
      },
      {
        label: "Rollover",
        value: rolloverCredits.toLocaleString(),
      },
      {
        label: "Pay As You Go",
        value: prepaidCredits.toLocaleString(),
      },
    ];
    const supportingNote =
      selectedPlanMetrics.personalRooms !== currentPersonalRooms
        ? `Also includes ${selectedPlanMetrics.personalRooms.toLocaleString()} personal room${selectedPlanMetrics.personalRooms === 1 ? "" : "s"}.`
        : undefined;
    const renewalRows: PendingSummaryRow[] =
      pendingPlan === "PERSONAL"
        ? [
            {
              label: "Plan",
              value: "Personal + Pay As You Go",
            },
          ]
        : [
            {
              label: "Charge",
              value: formatUsdStandard(selectedPlanMetrics.displayPrice),
            },
            {
              label: "Credits",
              value: selectedPlanMetrics.monthlyCredits.toLocaleString(),
            },
            {
              label: "Rollover",
              value: "Unused credits roll over",
              noWrap: true,
            },
          ];

    if (isManualPaidPlan) {
      return {
        title: `${automaticBillingScheduled ? "Automatic billing scheduled" : "Set up automatic billing"} for ${targetName}`,
        confirmLabel: automaticBillingScheduled ? "Billing scheduled" : "Set up automatic payments",
        confirmDisabled: automaticBillingScheduled || !billingCapabilities?.automaticBillingSetupEnabled,
        dueToday,
        dueDescription: chargeTodayPreview?.description || "Add a payment method to continue.",
        immediateText: `Your current ${PLAN_DISPLAY_NAMES[currentPlan]} plan stays active until automatic billing starts on ${automaticBillingSwitchText}.`,
        renewalDate: automaticBillingSwitchText,
        renewalRows,
      };
    }

    if (currentPlan === "PERSONAL") {
      return {
        title: `Start ${targetName}`,
        confirmLabel: "Continue to checkout",
        confirmDisabled: !billingCapabilities?.subscriptionCheckoutEnabled,
        dueToday,
        dueDescription: "Billed when you confirm checkout.",
        immediateHeading: "Available after upgrade",
        immediateValue: `${totalAvailableAfterUpgrade.toLocaleString()} credits`,
        immediateNote: usedThisCycle > 0 ? "Includes current cycle usage" : "Available immediately",
        secondaryHeading: "Breakdown",
        secondaryRows: upgradeBreakdownRows,
        immediateTone: "accent",
        supportingNote,
        renewalDate: nextRenewalDateLabel,
        renewalRows,
      };
    }

    // Both upgrades and paid→paid downgrades are instant with credit preview.
    return {
      title: isUpgrade ? `Upgrade to ${targetName}` : `Downgrade to ${targetName}`,
      confirmLabel: "Continue to billing",
      confirmDisabled: !billingCapabilities?.billingPortalEnabled,
      dueToday,
      dueDescription: isUpgrade ? "Prorated upgrade" : "Prorated change",
      immediateHeading: "Available after change",
      immediateValue: `${totalAvailableAfterUpgrade.toLocaleString()} credits`,
      immediateNote: "Includes current cycle usage",
      secondaryHeading: "Breakdown",
      secondaryRows: upgradeBreakdownRows,
      immediateTone: "accent",
      supportingNote,
      renewalDate: nextRenewalDateLabel,
      renewalRows,
    };
  }, [
    automaticBillingScheduled,
    automaticBillingSwitchText,
    billingCapabilities?.automaticBillingSetupEnabled,
    billingCapabilities?.billingPortalEnabled,
    billingCapabilities?.subscriptionCheckoutEnabled,
    chargeTodayPreview,
    currentPlan,
    currentMonthlyCredits,
    currentPersonalRooms,
    currentPlanCreditsRemaining,
    currentPrepaidCredits,
    currentRolloverCredits,
    currentSubscriptionCredits,
    isManualPaidPlan,
    isUpgrade,
    nextRenewalDateLabel,
    pendingPlan,
    previewData,
    selectedPlanMetrics,
  ]);

  const startCheckout = async (plan: Extract<PlanOption, "PRO" | "STUDIO">) => {
    if (!billingCapabilities?.subscriptionCheckoutEnabled) {
      toast({
        title: "Checkout unavailable",
        description: checkoutUnavailableReason,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmittingPlan(plan);
    trackDashboardEvent("billing_begin_checkout", {
      billing_flow: "subscription_checkout",
      plan_name: plan,
      current_plan: currentPlan,
    });

    try {
      const { data } = await createSubscriptionCheckoutSession({
        variables: { input: { plan } },
      });

      if (!data?.createSubscriptionCheckoutSession?.success || !data.createSubscriptionCheckoutSession.url) {
        throw new Error(data?.createSubscriptionCheckoutSession?.error || "Failed to start checkout.");
      }

      const checkoutWindow = window.open(data.createSubscriptionCheckoutSession.url, "_blank", "noopener,noreferrer");
      if (!checkoutWindow) {
        window.location.href = data.createSubscriptionCheckoutSession.url;
      }

      toast({
        title: "Checkout opened",
        description: `Complete your ${PLAN_DISPLAY_NAMES[plan]} upgrade in the new tab. Your plan updates automatically once payment is confirmed.`,
      });
      trackDashboardEvent("billing_result", {
        billing_flow: "subscription_checkout",
        result: "redirected",
        plan_name: plan,
        current_plan: currentPlan,
      });

      setPendingPlan(null);
    } catch (error) {
      trackDashboardEvent("billing_result", {
        billing_flow: "subscription_checkout",
        result: "failure",
        plan_name: plan,
        current_plan: currentPlan,
        error_message: error instanceof Error ? error.message : "checkout_failed",
      });
      toast({
        title: "Checkout unavailable",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSubmittingPlan(null);
    }
  };

  const startAutomaticBillingSetup = async () => {
    if (!billingCapabilities?.automaticBillingSetupEnabled) {
      toast({
        title: "Automatic billing unavailable",
        description: automaticBillingSetupUnavailableReason,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmittingPlan(currentPlan);
    trackDashboardEvent("billing_begin_checkout", {
      billing_flow: "automatic_billing_setup",
      current_plan: currentPlan,
    });

    try {
      const { data } = await createAutomaticBillingSetupSession();
      const response = data?.createAutomaticBillingSetupSession;

      if (!response?.success) {
        throw new Error(response?.error || "Failed to start automatic billing setup.");
      }

      if (response.url) {
        const setupWindow = window.open(response.url, "_blank", "noopener,noreferrer");
        if (!setupWindow) {
          window.location.href = response.url;
        }
      } else {
        toast({
          title: "Automatic billing scheduled",
          description: `Automatic billing is set to start on ${automaticBillingSwitchText}.`,
        });
      }

      setPendingPlan(null);
      trackDashboardEvent("billing_result", {
        billing_flow: "automatic_billing_setup",
        result: response.url ? "redirected" : "success",
        current_plan: currentPlan,
      });
    } catch (error) {
      trackDashboardEvent("billing_result", {
        billing_flow: "automatic_billing_setup",
        result: "failure",
        current_plan: currentPlan,
        error_message: error instanceof Error ? error.message : "automatic_billing_failed",
      });
      toast({
        title: "Automatic billing unavailable",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSubmittingPlan(null);
    }
  };

  const openBillingPortal = async (targetPlan?: PlanOption) => {
    if (!billingCapabilities?.billingPortalEnabled) {
      toast({
        title: "Billing portal unavailable",
        description: currentPlan === "PERSONAL" ? portalUnavailableReason : paidPlanUnavailableReason,
        variant: "destructive",
      });
      return;
    }

    const planForState: SubscriptionPlan = targetPlan ?? currentPlan;
    setIsSubmitting(true);
    setSubmittingPlan(planForState);
    trackDashboardEvent("billing_begin_checkout", {
      billing_flow: "billing_portal",
      current_plan: currentPlan,
      target_plan: targetPlan,
    });

    try {
      const { data } = await createBillingPortalSession({
        variables: { returnUrl: billingPortalReturnUrl("manage-plan"), plan: targetPlan },
      });

      if (!data?.createBillingPortalSession?.success || !data.createBillingPortalSession.url) {
        throw new Error(data?.createBillingPortalSession?.error || "Unable to open billing portal.");
      }

      const billingWindow = window.open(data.createBillingPortalSession.url, "_blank", "noopener,noreferrer");
      if (!billingWindow) {
        window.location.href = data.createBillingPortalSession.url;
      }

      if (targetPlan) {
        toast({
          title: isUpgradePath(targetPlan, currentPlan)
            ? `Upgrading to ${PLAN_DISPLAY_NAMES[targetPlan]}`
            : `Changing to ${PLAN_DISPLAY_NAMES[targetPlan]}`,
          description: "Complete the change in the new tab. Your plan updates once Stripe confirms.",
        });
      } else {
        toast({
          title: "Billing portal opened",
          description: "Manage your plan and payment in the new tab.",
        });
      }

      setPendingPlan(null);
      trackDashboardEvent("billing_result", {
        billing_flow: "billing_portal",
        result: "redirected",
        current_plan: currentPlan,
        target_plan: targetPlan,
      });
    } catch (error) {
      trackDashboardEvent("billing_result", {
        billing_flow: "billing_portal",
        result: "failure",
        current_plan: currentPlan,
        target_plan: targetPlan,
        error_message: error instanceof Error ? error.message : "billing_portal_failed",
      });
      toast({
        title: "Billing portal unavailable",
        description:
          error instanceof Error
            ? error.message
            : `Please try again or contact ${SUPPORT_EMAIL}.`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSubmittingPlan(null);
    }
  };

  const handleContactSales = async () => {
    setIsSubmitting(true);
    setSubmittingPlan("ENTERPRISE");
    trackDashboardEvent("billing_begin_checkout", {
      billing_flow: "enterprise_inquiry",
      current_plan: currentPlan,
      target_plan: "ENTERPRISE",
    });

    try {
      const { data } = await submitEnterpriseInquiry();
      const response = data?.submitEnterpriseInquiry;

      if (!response?.success) {
        throw new Error(response?.error || "Failed to send enterprise inquiry");
      }

      toast({
        title: "Sales notified",
        description: "The FlowLabs team has your details and will follow up shortly.",
      });
      trackDashboardEvent("billing_result", {
        billing_flow: "enterprise_inquiry",
        result: "success",
        current_plan: currentPlan,
        target_plan: "ENTERPRISE",
      });
    } catch (error) {
      trackDashboardEvent("billing_result", {
        billing_flow: "enterprise_inquiry",
        result: "failure",
        current_plan: currentPlan,
        target_plan: "ENTERPRISE",
        error_message: error instanceof Error ? error.message : "enterprise_inquiry_failed",
      });
      toast({
        title: "Could not contact sales",
        description:
          error instanceof Error
            ? error.message
            : "Please try again or use the support dialog.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setSubmittingPlan(null);
    }
  };

  const handleChoosePlan = async (plan: PlanOption) => {
    if (plan === currentPlan) return;

    if (currentPlan === "ENTERPRISE") {
      await handleContactSales();
      return;
    }

    if (isManualPaidPlan && automaticBillingScheduled) {
      return;
    }

    // Cancellation to Personal goes straight to Stripe billing portal.
    if (plan === "PERSONAL" && (currentPlan === "PRO" || currentPlan === "STUDIO")) {
      await openBillingPortal();
      return;
    }

    setPendingPlan(plan);
  };

  const handleConfirmPlanChange = async () => {
    if (!pendingPlan) return;

    if (currentPlan === "PERSONAL") {
      await startCheckout(pendingPlan as Extract<PlanOption, "PRO" | "STUDIO">);
      return;
    }

    if (isManualPaidPlan) {
      await startAutomaticBillingSetup();
      return;
    }

    await openBillingPortal(pendingPlan);
  };

  const actionLabelForPlan = (plan: PlanOption) => {
    if (currentPlan === "ENTERPRISE") return "Contact sales";
    if (isManualPaidPlan) return automaticBillingScheduled ? "Scheduled" : "Review change";
    if (isUpgradePath(plan, currentPlan)) return "Upgrade";
    return plan === "PERSONAL" ? "Cancel plan" : "Downgrade";
  };

  const actionDisabledForPlan = (plan: PlanOption) => {
    if (plan === currentPlan || currentPlan === "ENTERPRISE") {
      return false;
    }

    if (isManualPaidPlan && automaticBillingScheduled) {
      return true;
    }

    if (currentPlan === "PERSONAL") {
      return !billingCapabilities?.subscriptionCheckoutEnabled;
    }

    if (isManualPaidPlan) {
      return !billingCapabilities?.automaticBillingSetupEnabled;
    }

    return !billingCapabilities?.billingPortalEnabled;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-xl font-semibold">Plans</h2>
        {canOpenBillingPortal && !isManualPaidPlan ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => void openBillingPortal()}
            disabled={isSubmitting}
          >
            {isSubmitting && submittingPlan === currentPlan ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <ExternalLink className="mr-2 h-3.5 w-3.5" />
            )}
            Manage billing
          </Button>
        ) : null}
      </div>

      {showManualBillingBanner && isManualPaidPlan ? (
        <div className="rounded-lg border border-border bg-card p-4 text-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <p className="font-semibold text-foreground">
                  {automaticBillingScheduled ? "Automatic billing scheduled" : "Manual billing will move to automatic payments"}
                </p>
                <p className="text-muted-foreground">
                  {automaticBillingScheduled
                    ? `Your ${PLAN_DISPLAY_NAMES[currentPlan]} plan will switch to automatic billing on ${automaticBillingSwitchText}. Nothing changes in your access until then.`
                    : `This ${PLAN_DISPLAY_NAMES[currentPlan]} plan is still billed manually. Add a payment method now so automatic billing can start on ${automaticBillingSwitchText}.`}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!automaticBillingScheduled ? (
                <Button
                  variant="outline"
                  className="h-9 rounded-full border-border/60 bg-muted/30 px-4 text-sm font-medium text-orange-400 shadow-none hover:border-border hover:bg-muted/50 hover:text-orange-300"
                  onClick={() => void startAutomaticBillingSetup()}
                  disabled={isSubmitting}
                >
                  {isSubmitting && submittingPlan === currentPlan ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    "Set up automatic payments"
                  )}
                </Button>
              ) : null}
              {billingCapabilities?.billingPortalEnabled ? (
                <Button
                  variant="outline"
                  className="h-9 rounded-full border-border/60 bg-transparent px-4 text-sm font-medium text-muted-foreground shadow-none hover:border-border hover:bg-muted/40 hover:text-foreground"
                  onClick={() => void openBillingPortal()}
                  disabled={isSubmitting}
                >
                  {isSubmitting && submittingPlan === currentPlan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                  Update payment method
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {cancellationBanner ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-5 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-amber-500">{cancellationBanner.title}</p>
                <p className="text-sm text-muted-foreground">{cancellationBanner.body}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => void openBillingPortal()}
              disabled={isSubmitting}
              className="h-9 shrink-0 border-white/15 bg-transparent text-foreground hover:bg-white/5"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {cancellationBanner.action}
            </Button>
          </div>
        </div>
      ) : null}

      {availabilityNote ? (
        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          {availabilityNote}
        </div>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-3">
        {(currentPlan === "ENTERPRISE" ? (["PERSONAL", "PRO", "STUDIO"] as const) : PLAN_OPTIONS).map((plan) => (
          <PlanOptionCard
            key={plan}
            plan={plan}
            currentPlan={currentPlan}
            metrics={metricsByPlan[plan]}
            isPending={pendingPlan === plan}
            isSubmitting={isSubmittingByPlan[plan]}
            actionLabel={actionLabelForPlan(plan)}
            actionDisabled={actionDisabledForPlan(plan)}
            onChoosePlan={(nextPlan) => { void handleChoosePlan(nextPlan); }}
          />
        ))}
      </section>

      <Dialog
        open={Boolean(pendingPlan && pendingSummary)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingPlan(null);
          }
        }}
      >
        {pendingPlan && pendingSummary ? (
          <DialogContent className="w-[calc(100vw-3rem)] max-w-[62rem] overflow-hidden border-border bg-background p-0 text-foreground shadow-xl [&>button]:text-muted-foreground [&>button]:hover:text-foreground [&>button[data-state=open]]:bg-transparent">
            <div className="relative overflow-hidden">
              <div
                className={cn(
                  "pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br blur-3xl",
                  PLAN_GRADIENT_WASH[pendingPlan],
                )}
              />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.06),transparent_55%)]" />

              <div className="relative space-y-6 p-6 sm:p-8 md:p-10">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                  <DialogHeader className="max-w-2xl text-left">
                    <DialogTitle className="text-[2.6rem] font-semibold tracking-tight text-foreground">
                      {pendingSummary.title}
                    </DialogTitle>
                  </DialogHeader>

                  <section className="space-y-2 lg:min-w-[18rem] lg:pt-1 lg:text-right">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Pay today</p>
                    <p className="text-xs leading-5 text-muted-foreground">{pendingSummary.dueDescription}</p>
                    <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl">{pendingSummary.dueToday}</p>
                  </section>
                </div>

                <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(28rem,0.98fr)] lg:gap-10">
                  <section className="space-y-5">
                    {pendingSummary.immediateValue ? (
                      <div className="space-y-5">
                        <div className="space-y-1">
                          {pendingSummary.immediateHeading !== null ? (
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                              {pendingSummary.immediateHeading || "Available now"}
                            </p>
                          ) : null}
                          <div
                            className={cn(
                              pendingSummary.immediateTone === "default" ? "flex min-h-[2.5rem] items-end" : "",
                            )}
                          >
                            <p
                              className={cn(
                                "font-semibold tracking-tight",
                                pendingSummary.immediateTone === "default"
                                  ? "text-[1.28rem] font-medium leading-tight text-foreground sm:text-[1.45rem]"
                                  : "text-[2rem] text-primary sm:text-[2.35rem]",
                              )}
                            >
                              {pendingSummary.immediateValue}
                            </p>
                          </div>
                          {pendingSummary.immediateNote ? (
                            <p className="text-sm leading-6 text-muted-foreground">{pendingSummary.immediateNote}</p>
                          ) : null}
                        </div>

                        {pendingSummary.secondaryRows?.length ? (
                          <div className="space-y-3 border-t border-border/60 pt-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                              {pendingSummary.secondaryHeading || "Breakdown"}
                            </p>
                            <dl className="space-y-3">
                              {pendingSummary.secondaryRows.map((row) => (
                                <div
                                  key={`${row.label}-${row.value}`}
                                  className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline sm:gap-5"
                                >
                                  <div className="space-y-1">
                                    <dt className="text-sm leading-6 text-muted-foreground">{row.label}</dt>
                                    {row.helper ? (
                                      <p className="text-xs leading-5 text-muted-foreground">{row.helper}</p>
                                    ) : null}
                                  </div>
                                  <dd className="text-sm font-medium leading-6 text-foreground sm:text-right">
                                    {row.value}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        ) : null}

                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">What changes now</p>
                      </div>
                    )}

                    {pendingSummary.immediateText ? (
                      <p className="mt-5 max-w-2xl text-base leading-8 text-foreground">{pendingSummary.immediateText}</p>
                    ) : null}

                  </section>

                  <div className="space-y-8 lg:border-l lg:border-border/25 lg:pl-10">
                    <section className="space-y-5">
                      {pendingSummary.renewalSummary ? (
                        <div className="space-y-1">
                          {pendingSummary.renewalEyebrow !== null ? (
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                              {pendingSummary.renewalEyebrow || "At renewal"}
                            </p>
                          ) : null}
                          <div className="flex min-h-[2.5rem] items-end">
                            <p className="text-[1.28rem] font-medium leading-tight tracking-tight text-foreground sm:text-[1.45rem]">
                              {pendingSummary.renewalDate}
                            </p>
                          </div>
                          <p className="text-sm leading-6 text-muted-foreground">{pendingSummary.renewalSummary}</p>
                          {pendingSummary.renewalSubnote ? (
                            <p className="text-sm leading-6 text-muted-foreground">{pendingSummary.renewalSubnote}</p>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          <div className="space-y-1">
                            {pendingSummary.renewalEyebrow !== null ? (
                              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                                {pendingSummary.renewalEyebrow || "At renewal"}
                              </p>
                            ) : null}
                            <p className="text-[1.7rem] font-semibold tracking-tight text-foreground">
                              {pendingSummary.renewalDate}
                            </p>
                          </div>
                          <dl className="mt-5 space-y-4">
                          {pendingSummary.renewalRows.map((row, index) => (
                            <div
                              key={`${row.label}-${index}`}
                              className="grid gap-1.5 sm:grid-cols-[6.75rem_minmax(0,1fr)] sm:items-baseline sm:gap-5"
                            >
                              <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{row.label}</dt>
                              <dd
                                className={cn(
                                  "text-sm leading-6 sm:justify-self-end sm:text-right",
                                  row.noWrap ? "sm:min-w-max sm:whitespace-nowrap" : "",
                                  row.tone === "muted" ? "text-muted-foreground" : "text-foreground",
                                )}
                              >
                                {row.value}
                              </dd>
                            </div>
                          ))}
                          </dl>
                        </>
                      )}
                    </section>
                  </div>
                </div>

                <div className="flex flex-col gap-2 border-t border-border/70 pt-6 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  className="border-border bg-transparent text-foreground hover:bg-muted/40"
                  onClick={() => setPendingPlan(null)}
                >
                  Go back
                </Button>
                <Button
                  className={cn(
                    "border-0 text-white hover:opacity-90",
                    pendingPlan === "STUDIO"
                      ? "bg-gradient-to-r from-purple-500 to-indigo-500"
                      : pendingPlan === "PRO"
                        ? "bg-gradient-to-r from-amber-500 to-orange-500"
                        : "bg-primary",
                  )}
                  onClick={() => void handleConfirmPlanChange()}
                  disabled={pendingSummary.confirmDisabled || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Working...
                    </>
                  ) : (
                    pendingSummary.confirmLabel
                  )}
                </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>

      <EnterpriseRail
        isSubmitting={isSubmittingByPlan.ENTERPRISE}
        onContactSales={() => { void handleContactSales(); }}
      />

      {showCancellationAction ? (
        <div className="flex justify-end border-t border-border/60 pt-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void openBillingPortal()}
            disabled={isSubmitting}
            className="text-xs text-red-500/70 hover:bg-red-500/10 hover:text-red-500"
          >
            {isSubmitting && submittingPlan === currentPlan ? "Opening..." : "Cancel subscription"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
